import React, { useMemo } from 'react';
import { Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import useResponsiveness from '@/hooks/useResponsiveness';
import DropdownMenuItemDivider from '@/components/DropdownMenu/DropdownMenuItemDivider';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import { useTranslation } from '@/hooks/useTranslation';

function renderWebItems(items) {
  return items.map((item) => (
    <DropdownMenuItem
      key={item.key}
      onSelect={() => item.onSelect?.()}
      className="DropdownMenuItem"
      icon={item.icon}
      title={item.title}
      isDanger={item.isDanger}
    />
  ));
}

function renderNativeItems(items, colors) {
  return items.map((item) => (
    <DropdownMenu.Item
      key={item.key}
      destructive={item.isDanger}
      onSelect={() => item.onSelect?.()}
    >
      <DropdownMenu.ItemIcon ios={{
        name: item.iosIcon,
        color: item.isDanger ? undefined : colors.text_color_primary,
      }} />
      <DropdownMenu.ItemTitle>
        {item.title}
      </DropdownMenu.ItemTitle>
    </DropdownMenu.Item>
  ));
}

function CollectionDropdown({
  TriggerComponent,
  noRoot,
  handleSettings,
  handleCustomInstructions,
  handleMemorySettings,
  handleAddFiles,
  handleCreateTask,
  handleChangeIcon,
  handleRename,
  handleDelete,
  handleNewSubcollection,
  handleMove,
  isLongPress,
  title,
  align,
  side,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();

  const settingItems = useMemo(() => [
    handleCustomInstructions && {
      key: 'custom-instructions',
      icon: 'sliders',
      iosIcon: 'slider.horizontal.3',
      title: t('pages.collections.customInstructions'),
      onSelect: handleCustomInstructions,
    },
    handleSettings && {
      key: 'settings',
      icon: 'gear',
      iosIcon: 'gearshape',
      title: t('collectionSettings.chip'),
      onSelect: handleSettings,
    },
    handleMemorySettings && {
      key: 'memory',
      icon: 'brain',
      iosIcon: 'brain',
      title: t('pages.collections.memorySettings'),
      onSelect: handleMemorySettings,
    },
  ].filter(Boolean), [handleCustomInstructions, handleSettings, handleMemorySettings, t]);

  const contentItems = useMemo(() => [
    handleAddFiles && {
      key: 'add-files',
      icon: 'file-circle-plus',
      iosIcon: 'doc.badge.plus',
      title: t('pages.collections.addFiles'),
      onSelect: handleAddFiles,
    },
    handleCreateTask && {
      key: 'create-task',
      icon: 'clock',
      iosIcon: 'clock',
      title: t('pages.collections.createTask'),
      onSelect: handleCreateTask,
    },
    handleNewSubcollection && {
      key: 'new-subcollection',
      icon: 'folder-plus',
      iosIcon: 'folder.badge.plus',
      title: t('dropdowns.newSubcollection'),
      onSelect: handleNewSubcollection,
    },
  ].filter(Boolean), [handleAddFiles, handleCreateTask, handleNewSubcollection, t]);

  const manageItems = useMemo(() => [
    {
      key: 'change-icon',
      icon: 'icons',
      iosIcon: 'square.grid.2x2',
      title: t('dropdowns.changeIcon'),
      onSelect: handleChangeIcon,
    },
    handleMove && {
      key: 'move-collection',
      icon: 'folder-arrow-down',
      iosIcon: 'folder',
      title: t('dropdowns.moveCollection'),
      onSelect: handleMove,
    },
    {
      key: 'rename',
      icon: 'pencil',
      iosIcon: 'pencil',
      title: t('dropdowns.renameCollection'),
      onSelect: handleRename,
    },
  ].filter(Boolean), [handleChangeIcon, handleMove, handleRename, t]);

  const deleteItem = {
    key: 'delete',
    icon: 'trash',
    iosIcon: 'trash',
    title: t('dropdowns.deleteCollection'),
    onSelect: handleDelete,
    isDanger: true,
  };

  const content = (
    <>
      {TriggerComponent && (
        <DropdownMenu.Trigger action={isLongPress ? 'longPress' : undefined}>
          <TriggerComponent />
        </DropdownMenu.Trigger>
      )}
      <DropdownMenu.Content
        className={Platform.OS === 'web' ? "DropdownMenuContent" : undefined}
        side={side}
        align={align || (isXS ? 'center' : 'end')}
        sideOffset={isXS ? 4 : 5}
      >
        {Platform.OS === 'web' ? (
          <>
            {title && (
              <>
                <DropdownMenuLabel key="collection-title" text={title} />
                <DropdownMenuItemDivider key="collection-title-divider" />
              </>
            )}
            {renderWebItems(settingItems)}
            {settingItems.length > 0 && (contentItems.length > 0 || manageItems.length > 0) && (
              <DropdownMenuItemDivider key="collection-settings-divider" />
            )}
            {renderWebItems(contentItems)}
            {contentItems.length > 0 && (
              <DropdownMenuItemDivider key="collection-content-divider" />
            )}
            {renderWebItems(manageItems)}
            <DropdownMenuItemDivider key="collection-delete-divider" />
            {renderWebItems([deleteItem])}
          </>
        ) : (
          <>
            {title && (
              <>
                <DropdownMenu.Item key="collection-title" disabled>
                  <DropdownMenu.ItemTitle numberOfLines={1}>{title}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
              </>
            )}
            {renderNativeItems(settingItems, colors)}
            {settingItems.length > 0 && (contentItems.length > 0 || manageItems.length > 0) && (
              <DropdownMenu.Separator />
            )}
            {renderNativeItems(contentItems, colors)}
            {contentItems.length > 0 && (
              <DropdownMenu.Separator />
            )}
            {renderNativeItems(manageItems, colors)}
            <DropdownMenu.Separator />
            {renderNativeItems([deleteItem], colors)}
          </>
        )}
      </DropdownMenu.Content>
    </>
  );

  if (noRoot) {
    return content;
  }

  return (
    <DropdownMenu.Root>
      {content}
    </DropdownMenu.Root>
  );
}

export default CollectionDropdown;
